import Grid from '@material-ui/core/Grid';
import GridFormField from 'shared/components/GridFormField';
import React from 'react';
import removeHTML from 'shared/utils/removeHTML';
import Typography from '@material-ui/core/Typography';
import {
	formatPhone,
	parsePhone,
	formatZipPostal,
	normalizeZipPostal,
} from 'shared/utils/formatting';
import {
	maxStrLength,
	phoneNumberValidation,
	settingValidation,
	restrictedTextInputValidation,
	zipPostalValidation,
} from 'shared/utils/validations';
import { OPTIONAL, SKIP } from 'redux/domain/Settings/constants';
import { PropTypes } from 'prop-types';
import { STATES } from './constants';

class PersonalReferenceFields extends React.Component {
	constructor() {
		super();
		this.referenceFieldSettings = this.referenceFieldSettings.bind(this);
		this.referenceFieldValidation = this.referenceFieldValidation.bind(this);
		this.formatZipPostalField = this.formatZipPostalField.bind(this);
		this.normalizeZipPostalField = this.normalizeZipPostalField.bind(this);
	}

	referenceFieldSettings(fieldSettings) {
		const { index, settings } = this.props;
		const minRequired = settings.fieldAndFooterSettings.ApplicationPageSettings.RequiredNumberofReferencesOnCreditApplication;
		if (index < minRequired || fieldSettings.fieldStatus === SKIP) {
			return fieldSettings;
		}
		return { ...fieldSettings, fieldStatus: OPTIONAL };
	}

	referenceFieldValidation(fieldSettings) {
		return settingValidation(this.referenceFieldSettings(fieldSettings));
	}

	// eslint-disable-next-line class-methods-use-this
	formatZipPostalField(value) {
		return formatZipPostal(value);
	}

	// eslint-disable-next-line class-methods-use-this
	normalizeZipPostalField(value) {
		return normalizeZipPostal(value);
	}

	render() {
		const { disableAll, member, settings } = this.props;
		return (
			<div style={{ width: '100%' }}>
				<Grid container spacing={5}>
					<GridFormField
						fieldSettings={this.referenceFieldSettings(settings.primary.referenceName)}
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.ReferenceNameLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.ReferenceNameDescription}
						name={`${member}.name`}
						validation={[
							this.referenceFieldValidation(settings.primary.referenceName),
							maxStrLength(50),
							restrictedTextInputValidation,
						]}
						disabled={disableAll}
					/>
					<GridFormField
						fieldSettings={this.referenceFieldSettings(settings.primary.referenceType)}
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.ReferenceTypeLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.ReferenceTypeDescription}
						name={`${member}.referenceType`}
						validation={[
							this.referenceFieldValidation(settings.primary.referenceType),
							maxStrLength(30),
						]}
						disabled={disableAll}
					/>
					<GridFormField
						fieldSettings={this.referenceFieldSettings(settings.primary.referencePhone)}
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.ReferencePhoneLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.ReferencePhoneDescription}
						name={`${member}.phone`}
						validation={[
							this.referenceFieldValidation(settings.primary.referencePhone),
							phoneNumberValidation('US'),
						]}
						disabled={disableAll}
						format={formatPhone('US')}
						parse={parsePhone('US')}
					/>
					<GridFormField
						fieldSettings={this.referenceFieldSettings(settings.primary.referenceAddress1)}
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.ReferenceAddress1Label}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.ReferenceAddress1Description}
						name={`${member}.address1`}
						validation={[
							this.referenceFieldValidation(settings.primary.referenceAddress1),
							maxStrLength(30),
						]}
						disabled={disableAll}
					/>
					<GridFormField
						fieldSettings={this.referenceFieldSettings(settings.primary.referenceAddress2)}
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.ReferenceAddress2Label}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.ReferenceAddress2Description}
						name={`${member}.address2`}
						validation={[
							this.referenceFieldValidation(settings.primary.referenceAddress2),
							maxStrLength(30),
						]}
						disabled={disableAll}
					/>
					<GridFormField
						fieldSettings={this.referenceFieldSettings(settings.primary.referenceCity)}
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.ReferenceCityLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.ReferenceCityDescription}
						name={`${member}.city`}
						validation={[
							this.referenceFieldValidation(settings.primary.referenceCity),
							maxStrLength(30),
						]}
						disabled={disableAll}
					/>
					<GridFormField
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.ReferenceStateLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.ReferenceStateDescription}
						fieldSettings={this.referenceFieldSettings(settings.primary.referenceState)}
						select
						menuOptions={STATES}
						name={`${member}.state`}
						validation={this.referenceFieldValidation(settings.primary.referenceState)}
						disabled={disableAll}
					/>
					<GridFormField
						fieldSettings={this.referenceFieldSettings(settings.primary.referenceZip)}
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.ReferencePostalLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.ReferencePostalDescription}
						name={`${member}.zipCode`}
						validation={[
							zipPostalValidation,
							this.referenceFieldValidation(settings.primary.referenceZip),
							maxStrLength(10),
						]}
						format={this.formatZipPostalField}
						normalize={this.normalizeZipPostalField}
						disabled={disableAll}
					/>
				</Grid>
				<Typography type="caption" component="div" style={{ paddingTop: '20px' }}>
					<div
						// eslint-disable-next-line react/no-danger
						dangerouslySetInnerHTML={{
							__html: removeHTML(settings.fieldAndFooterSettings.ApplicationPageSettings.PersonalReferenceFooter),
						}}
					/>
				</Typography>
			</div>
		);
	}
}

PersonalReferenceFields.propTypes = {
	disableAll: PropTypes.bool,
	member: PropTypes.string.isRequired,
	index: PropTypes.number.isRequired,
	settings: PropTypes.shape({
		primary: PropTypes.objectOf(
			PropTypes.oneOfType([
				PropTypes.shape({
					fieldId: PropTypes.string,
					fieldStatus: PropTypes.string,
					fieldSubHeaderType: PropTypes.string,
				}),
				PropTypes.number,
			]),
		),
		fieldAndFooterSettings: PropTypes.shape({
			ApplicationPageSettings: PropTypes.shape({
				RequiredNumberofReferencesOnCreditApplication: PropTypes.number.isRequired,
			}),
		}),
	}),
};

PersonalReferenceFields.defaultProps = {
	disableAll: false,
	settings: {},
};

export default PersonalReferenceFields;
